<template>
  <div
    :class="{'news-toolbar--blur': scrollTop >= 5}"
    class="news-toolbar"
    :style="{'width': paginatorWidth + 'px'}"
  >
    <div class="news-toolbar__select-all">
      <Checkbox
        v-model="isAllPageCardChosen"
        @change="emit('toggle-all-cards')"
      >
        <template #default>
          <span class="news-toolbar__select-all-label">
            {{ t('news_page_pagination.chose_all') }}
          </span>
        </template>
      </Checkbox>
    </div>
    <div
      v-if="chosenCardList.length > 0"
      class="news-toolbar__actions"
    >
      <button
        v-tooltip:bottom:1="t('news_page_toolbar.tooltip.favorite')"
        class="news-toolbar__button"
        @click="addToFavoriteCard(chosenCardList)"
      >
        <ToolbarBookmarkIcon class="news-toolbar__icon" />
      </button>
      <button
        v-tooltip:bottom:1="t('news_page_toolbar.tooltip.download')"
        class="news-toolbar__button"
        @click="downloadCard(chosenCardList)"
      >
        <ToolbarDownloadIcon class="news-toolbar__icon" />
      </button>
      <button
        v-tooltip:bottom:1="t('news_page_toolbar.tooltip.delete')"
        class="news-toolbar__button"
        @click="isShowDeleteCardModal = true"
      >
        <ToolbarTrashIcon class="news-toolbar__icon" />
      </button>
      <button
        class="news-toolbar__button news-toolbar__button-chosen"
        @click="emit('reset-card-list')"
      >
        {{ t('news_page_toolbar.tooltip.chosen') }}: {{ chosenCardList.length }}
        <ToolbarCrossIcon class="news-toolbar__icon" />
      </button>
    </div>

    <Modal
      v-if="isShowDeleteCardModal"
      teleport="#app"
      :confirm-text="t('news_page_popup_delete.button.accept_delete')"
      :title="t('news_page_toolbar.delete_modal.title')"
      :use-cross-icon="false"
      @cancel="isShowDeleteCardModal = false"
      @confirm="deleteCard(chosenCardList)"
      @close="isShowDeleteCardModal = false"
    >
      {{ t('news_page_toolbar.delete_modal.text') }}
    </Modal>
  </div>
</template>

<script setup>
import { t } from "@/controllerLK/AvalLocalization";
import {computed, onBeforeUnmount, ref, toRef, watch, defineProps, defineEmits} from "vue";
import ToolbarBookmarkIcon from "@/components/Icons/ToolbarBookmarkIcon.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed";
import { useNotificationsStore } from "@/store/notifications";
import ToolbarDownloadIcon from "@/components/Icons/ToolbarDownloadIcon.vue";
import ToolbarTrashIcon from "@/components/Icons/ToolbarTrashIcon.vue";
import ToolbarCrossIcon from "@/components/Icons/ToolbarCrossIcon.vue";

const props = defineProps({
  chosenCardList: {
    type: Array,
    required: true,
    default: () => []
  },
  container: {
    type: [Object, null],
    required: true
  },
  isAllPageCardChosen: {
    type: Boolean,
    default: false
  }
});

const container = toRef(props, "container");

const emit = defineEmits(["toggle-all-cards", "reset-card-list"]);

// Сторы
const newsFeedStore = useNewsFeedStore();
const notificationStore = useNotificationsStore();

// Отображение окна подтверждения удаления карточек
const isShowDeleteCardModal = ref(false);

// Добавить карточки в избранное
const addToFavoriteCard = async (list) => {
  if (list.length === 0) return;
  newsFeedStore.loading = true;

  for (const item of list) { await newsFeedStore.setFavorite(item, true); }

  newsFeedStore.loading = false;
  notificationStore.createNotify(`${t("news_page_toolbar.toast.favorite")}`, "success");
};

// Скачать карточки
const downloadCard = async (list) => {
  if (list.length === 0) return;
  await newsFeedStore.filesDownload(props.chosenCardList);
};

// Удаление карточек.
const deleteCard = async (list) => {
  if (list.length === 0) return;
  newsFeedStore.loading = true;
  isShowDeleteCardModal.value = false;

  emit("reset-card-list");

  for (const item of list) await newsFeedStore.deleteCard(item);

  newsFeedStore.loading = false;
  await new Promise(res => setTimeout(res, 1500)).then(() => newsFeedStore.getNews());
  notificationStore.createNotify(`${t("news_page_toolbar.toast.delete")}`, "success");
};

// Чекбокс "Выбрать все" - синхронизирован с prop от родителя
const isAllPageCardChosen = computed(() => props.isAllPageCardChosen);

// Расчет ширины тулбара под разный масштаб
const paginatorWidth = ref(0);
let ro;

// Расчет скролла для наложения блюра на тулбар
const scrollTop = ref(0);

const onScroll = (e) => {
  const el = e.target || e;
  scrollTop.value = el.scrollTop;
};

watch(container, (el, prev) => {
  // убираем старый наблюдатель
  if (prev && ro) {
    ro.disconnect();
  }
  // вешаем новый
  if (el) {
    ro = new ResizeObserver(([entry]) => {
      // Округление вверх 2-ух знаков после запятой
      const raw = entry.contentRect.width;
      const roundedUp = Math.ceil(raw * 100) / 100;
      paginatorWidth.value = parseFloat(roundedUp.toFixed(2));
    });
    ro.observe(el);
  }
}, { immediate: true });

onBeforeUnmount(() => {
  ro?.disconnect();
});

watch(container, (el, prev) => {
  if (prev) prev.removeEventListener("scroll", onScroll);
  if (el) {
    onScroll(el);
    el.addEventListener("scroll", onScroll, { passive: true });
  }
}, { immediate: true });
</script>
<style lang="scss" scoped>
.news-toolbar {
  position: absolute;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
  color: var(--text-text-pur);
  gap: 10px;
  width: 100%;

  &--blur {
    background: linear-gradient(0deg, rgba(238, 241, 244, 0.69) 0%, #EEF1F4 100%);
    box-shadow: 0 5px 7px 0 #7090B014;
    backdrop-filter: blur(6px);
  }

  &__select-all {
    display: flex;
    align-items: center;
    height: 30px;

    &-label {
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      color: var(--text-text-pur);
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 30px;
    cursor: pointer;
    user-select: none;
    border-radius: 50px;

    &-chosen {
      padding: 0 14px;
      font-weight: 500;
      font-size: 14px;
      line-height: 17px;
      gap: 4px;
      align-items: center;
    }

    &:hover {
      background-color: var(--icon-button-ghost-hover, #F7F8FD); // TODO: Нет переменной в UI Kit.
    }
  }

  &__icon {
    width: 17px;
    height: 17px;
    color: var(--icon-button-ghost-icon, #7B87A5) // TODO: Нет переменной в UI Kit.
  }
}
</style>